'use client';

import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import { hasTravelSummaryDetails } from '@/components/blocks/travel-summary/travel-summary.utils';
import BookingPriceDetailsModal from '@/components/organisms/booking-price-details-modal/booking-price-details-modal';
import ImageSlider from '@/components/organisms/image-slider/image-slider';
import TravelPlanModal from '@/components/organisms/travel-plan-modal/travel-plan-modal';
import Link from '@/components/utilities/link/link';
import useQueryTravelSummary from '@/integrations/content/content-modules/travel-summary/travel-summary.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import styles from './travel-summary.module.scss';

export interface TravelSummaryProps {
  /** The UUID of the module */
  id: Content.Components['TravelSummaryHeadlessModuleDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['TravelSummaryHeadlessModuleDto']['pageId'];
  /** The parameters for the booking */
  parameters: Content.Components['TravelSummaryHeadlessModuleDto']['parameters'];
}

const TravelSummary = ({ id, pageId, parameters }: TravelSummaryProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();
  const [travelSummary, setTravelSummary] =
    useState<Content.Properties.TravelSummary>();
  const [isOpenBookingPriceDetails, setIsOpenBookingPriceDetails] =
    useState(false);
  const [isOpenTravelPlan, setIsOpenTravelPlan] = useState(false);
  const identifiers = { ...parameters, id, pageId };

  const {
    data: dataTravelSummary,
    isLoading: isLoadingTravelSummary,
    isFetching: isFetchingTravelSummary,
    refetch: refetchTravelSummary,
  } = useQueryTravelSummary({
    parameters: identifiers,
  });

  const hasNoData = !isFetchingTravelSummary && !dataTravelSummary;

  const getFlightText = (): string => {
    if (!travelSummary) return '';

    const homeboundAirportName = travelSummary.homeboundAirportName;
    const outboundAirportName = travelSummary.outboundAirportName;

    return `${outboundAirportName} - ${homeboundAirportName}`;
  };

  useEffect(() => {
    refetchTravelSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dataTravelSummary) return;

    setTravelSummary(dataTravelSummary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTravelSummary]);

  if (isLoadingTravelSummary) {
    return (
      <div className={classNames(styles.component, styles.isLoading)}>
        <div className={styles.contentWrapper}>
          <div className={styles.imageWrapper}>
            <SkeletonLoader height="100%" width="100%" />
          </div>

          <div className={styles.content}>
            <div className={styles.items}>
              <SkeletonLoader
                className={classNames(styles.headline, styles.loader)}
                height="22px"
                width="100%"
              />

              <div className={styles.item}>
                <Icon name="Location" size={14} />

                <SkeletonLoader
                  className={styles.loader}
                  height="17px"
                  width="100%"
                />
              </div>

              <div className={styles.item}>
                <Icon name="Calendar" size={14} />

                <SkeletonLoader
                  className={styles.loader}
                  height="17px"
                  width="100%"
                />
              </div>

              <div className={styles.item}>
                <Icon name="User" size={14} />

                <SkeletonLoader
                  className={styles.loader}
                  height="17px"
                  width="100%"
                />
              </div>

              <div className={styles.item}>
                <Icon name="Bed" size={14} />

                <SkeletonLoader
                  className={styles.loader}
                  height="17px"
                  width="100%"
                />
              </div>

              <div className={styles.item}>
                <Icon name="Food" size={14} />

                <SkeletonLoader
                  className={styles.loader}
                  height="17px"
                  width="100%"
                />
              </div>

              <div className={styles.item}>
                <Icon name="PlaneTakeOff" size={14} />

                <SkeletonLoader
                  className={styles.loader}
                  height="17px"
                  width="100%"
                />
              </div>
            </div>

            <div className={styles.divider} />

            <div className={styles.priceWrapper}>
              <div className={styles.priceText}>
                <Typography tagStyle="labelSmall">
                  {t('items.modules.bookingFlow.inTotal')}
                </Typography>
              </div>

              <div className={styles.price} style={{ width: '120px' }}>
                <SkeletonLoader
                  className={styles.loader}
                  height="24px"
                  width="100%"
                />

                <div className={styles.priceDetails}>
                  <Icon name="Info" size={14} />

                  <SkeletonLoader
                    className={styles.loader}
                    height="17px"
                    width="50%"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (hasNoData) return null;

  return (
    <div className={styles.component}>
      {travelSummary && (
        <React.Fragment>
          <div className={styles.contentWrapper}>
            {travelSummary.hotelImages && (
              <div className={styles.imageWrapper}>
                <ImageSlider
                  ignoreMinimumSize
                  images={
                    travelSummary.hotelImages
                      ? travelSummary.hotelImages.map(image => ({
                          altText: image.altText,
                          url: image.url,
                        }))
                      : []
                  }
                />
              </div>
            )}

            <div className={styles.content}>
              <div className={styles.items}>
                {travelSummary.hotelName && (
                  <Typography
                    className={styles.headline}
                    tagStyle="bodyMediumBold"
                  >
                    {travelSummary.hotelName}
                  </Typography>
                )}

                {travelSummary.destinationName && (
                  <div className={styles.item}>
                    <Icon name="Location" size={14} />

                    <Typography className={styles.text} tagStyle="bodySmall">
                      {travelSummary.destinationName}
                    </Typography>
                  </div>
                )}

                {travelSummary.period && (
                  <div className={styles.item}>
                    <Icon name="Calendar" size={14} />

                    <Typography className={styles.text} tagStyle="bodySmall">
                      {travelSummary.period}
                    </Typography>
                  </div>
                )}

                {travelSummary.travelers && (
                  <div className={styles.item}>
                    <Icon name="User" size={14} />

                    <Typography className={styles.text} tagStyle="bodySmall">
                      {travelSummary.travelers}
                    </Typography>
                  </div>
                )}

                {travelSummary.roomName && (
                  <div className={styles.item}>
                    <Icon name="Bed" size={14} />

                    <Typography className={styles.text} tagStyle="bodySmall">
                      {travelSummary.roomName}
                    </Typography>
                  </div>
                )}

                {travelSummary.highlightedProducts.length > 0 &&
                  travelSummary.highlightedProducts.map(
                    (highlightedProduct, highlightedProductIndex) => (
                      <div
                        key={highlightedProductIndex}
                        className={styles.item}
                      >
                        <Icon
                          name={highlightedProduct.icon as IconName}
                          size={14}
                        />

                        <Typography
                          className={styles.text}
                          tagStyle="bodySmall"
                        >
                          {highlightedProduct.headline}
                        </Typography>
                      </div>
                    ),
                  )}

                {getFlightText() && (
                  <div className={styles.item}>
                    <Icon
                      name={dataTravelSummary?.departureIcon as IconName}
                      size={14}
                    />

                    <div className={styles.flightInfo}>
                      <Typography className={styles.text} tagStyle="bodySmall">
                        {getFlightText()}
                      </Typography>

                      {hasTravelSummaryDetails(travelSummary) && (
                        <Link
                          className={styles.flightDetailsLink}
                          onClick={() => {
                            setIsOpenTravelPlan(true);
                          }}
                        >
                          <Typography tagStyle="bodySmall">
                            {t('items.modules.bookingFlow.travelPlanDetails')}
                          </Typography>
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.divider} />

              {!!travelSummary.totalPrice && (
                <div className={styles.priceWrapper}>
                  <Typography
                    className={styles.priceText}
                    tagStyle="labelSmall"
                  >
                    {t('items.modules.bookingFlow.inTotal')}
                  </Typography>

                  {isFetchingTravelSummary ? (
                    <div className={styles.price} style={{ width: '120px' }}>
                      <SkeletonLoader
                        className={styles.loader}
                        height="24px"
                        width="100%"
                      />

                      <div className={styles.priceDetails}>
                        <Icon name="Info" size={14} />

                        <SkeletonLoader
                          className={styles.loader}
                          height="17px"
                          width="50%"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={styles.price}>
                      <Typography tagStyle="priceMedium">
                        {currencyFormatter(travelSummary.totalPrice)}
                      </Typography>

                      <div className={styles.priceDetails}>
                        <Icon className={styles.info} name="Info" size={14} />

                        <Link
                          className={styles.priceDetailsLink}
                          onClick={() => {
                            setIsOpenBookingPriceDetails(true);
                          }}
                        >
                          <Typography tagStyle="bodySmall">
                            {t('items.modules.bookingFlow.priceDetails')}
                          </Typography>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {hasTravelSummaryDetails(travelSummary) && (
            <TravelPlanModal
              headline={travelSummary.travelSummaryDetailsDto.headline}
              isLoading={isLoadingTravelSummary}
              isOpen={isOpenTravelPlan}
              onClose={setIsOpenTravelPlan}
              subHeadline={travelSummary.travelSummaryDetailsDto.subHeadline}
              text={travelSummary.travelSummaryDetailsDto.text}
              travelPlan={travelSummary.travelSummaryDetailsDto.travelPlan}
            />
          )}

          {travelSummary.bookingPriceDetails && (
            <BookingPriceDetailsModal
              bookingPriceDetails={travelSummary.bookingPriceDetails}
              identifiers={identifiers}
              isOpen={isOpenBookingPriceDetails}
              onClose={setIsOpenBookingPriceDetails}
            />
          )}
        </React.Fragment>
      )}
    </div>
  );
};

TravelSummary.displayName = 'TravelSummary';

export default TravelSummary;
